/**
 * PR-AdminUsers-6 — /admin/audit-log
 * Tabla de audit log con filtros por acción y tipo de target.
 */
import { useState } from "react";
import { useAdminAuditLog } from "@/hooks/useAdminUsers";

const ACTIONS = [
  "create", "update", "delete",
  "assign", "lock_user", "unlock_user",
  "reset_password", "disable_2fa", "reset_2fa",
  "activate_feature",
];

const TARGET_TYPES = ["segment", "area", "feature", "user", "user_segments", "user_area_roles", "user_global_roles"];

export function AdminAuditLogPage() {
  const [action, setAction] = useState("");
  const [targetType, setTargetType] = useState("");

  const log = useAdminAuditLog({
    action: action || undefined,
    target_type: targetType || undefined,
    limit: 100,
  });

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold">Audit Log</h1>
        <p className="text-sm text-muted-foreground">
          Registro de todas las acciones administrativas.
        </p>
      </div>

      {/* Filtros */}
      <div className="flex gap-3">
        <select
          value={action}
          onChange={e => setAction(e.target.value)}
          className="rounded-md border bg-background px-3 py-2 text-sm"
        >
          <option value="">Todas las acciones</option>
          {ACTIONS.map(a => (
            <option key={a} value={a}>{a}</option>
          ))}
        </select>
        <select
          value={targetType}
          onChange={e => setTargetType(e.target.value)}
          className="rounded-md border bg-background px-3 py-2 text-sm"
        >
          <option value="">Todos los tipos</option>
          {TARGET_TYPES.map(t => (
            <option key={t} value={t}>{t}</option>
          ))}
        </select>
      </div>

      {log.isPending && <p className="text-sm text-muted-foreground">Cargando…</p>}
      {log.isError && <p className="text-sm text-destructive">Error al cargar el audit log.</p>}

      {log.data && (
        <>
          <p className="text-xs text-muted-foreground">{log.data.total} entradas</p>
          <div className="rounded-lg border overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-muted/50">
                <tr>
                  <th className="px-4 py-3 text-left font-medium">Timestamp</th>
                  <th className="px-4 py-3 text-left font-medium">Acción</th>
                  <th className="px-4 py-3 text-left font-medium">Tipo</th>
                  <th className="px-4 py-3 text-left font-medium">Target ID</th>
                  <th className="px-4 py-3 text-left font-medium">Cambio</th>
                </tr>
              </thead>
              <tbody>
                {log.data.items.map(entry => (
                  <tr key={entry.id} className="border-t hover:bg-accent/30">
                    <td className="px-4 py-2.5 text-xs text-muted-foreground whitespace-nowrap">
                      {new Date(entry.timestamp).toLocaleString("es-MX")}
                    </td>
                    <td className="px-4 py-2.5">
                      <span className="rounded bg-muted px-2 py-0.5 font-mono text-xs">
                        {entry.action}
                      </span>
                    </td>
                    <td className="px-4 py-2.5 text-xs text-muted-foreground font-mono">
                      {entry.target_type}
                    </td>
                    <td className="px-4 py-2.5 text-xs text-muted-foreground font-mono truncate max-w-[200px]">
                      {entry.target_id ?? "—"}
                    </td>
                    <td className="px-4 py-2.5 text-xs text-muted-foreground font-mono truncate max-w-[280px]">
                      {entry.after_value != null
                        ? JSON.stringify(entry.after_value)
                        : "—"}
                    </td>
                  </tr>
                ))}
                {log.data.items.length === 0 && (
                  <tr>
                    <td colSpan={5} className="px-4 py-8 text-center text-muted-foreground">
                      Sin entradas con los filtros aplicados.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
